import * as React from 'react';

import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTagIcon } from '@/hooks/useTagIcon';

/**
 * Minimal shape every surface that displays a tag must provide. The icon
 * comes from `tags_custom.icon` (a filename on the EFS-mounted tag_icons
 * folder) and the description is the per-patient note from
 * `patients_tags_custom.description`. Title is the tag definition name.
 *
 * Surfaces use slightly different field names on the wire (e.g. the
 * appointment popup ships `id` + `iconUrl`), so callers are expected to
 * adapt to this shape before rendering.
 */
export interface TagIconTag {
  tagsCustomID: number;
  title: string;
  description?: string;
  icon?: string;
  createDate?: string | null;
  addedByName?: string | null;
}

/**
 * Visual style of a single tag rendering.
 *
 * - `pill` (default): V5 colored chip with the uppercase title and the
 *   tag icon to the right. Used on the patient header, demographics
 *   tab, sidebar profile, and calendar popover.
 * - `icon`: legacy-style 20×20 icon image only, with title/description
 *   surfaced in the hover tooltip. Used on the Patients list grid where
 *   space is tight and many tags share a row.
 */
export type TagBadgeVariant = 'pill' | 'icon';

export interface TagIconBadgeProps {
  tag: TagIconTag;
  variant?: TagBadgeVariant;
  /** Diameter (px) of the icon image rendered inside the pill or as the icon-only badge. */
  iconSize?: number;
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLSpanElement>) => void;
  /**
   * Optional decoration rendered after the icon (e.g. a `*` marker when
   * a per-patient note exists). Lives inside the tooltip trigger so the
   * hover surface includes it.
   */
  trailing?: React.ReactNode;
}

function formatTagDate(raw: string): string {
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  let h = d.getHours();
  const min = String(d.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${mm}/${dd}/${yyyy} ${h}:${min} ${ampm}`;
}

/**
 * Pill color rotation. Keyed deterministically on `tagsCustomID` so a
 * given tag always renders in the same color across every surface
 * (patient header, demographics, sidebar, calendar popover).
 */
const TAG_COLORS = [
  'bg-red-50 text-red-600 border-red-200',
  'bg-amber-50 text-amber-600 border-amber-200',
  'bg-green-50 text-green-600 border-green-200',
  'bg-blue-50 text-blue-600 border-blue-200',
  'bg-purple-50 text-purple-600 border-purple-200',
  'bg-teal-50 text-teal-600 border-teal-200',
  'bg-pink-50 text-pink-600 border-pink-200',
  'bg-indigo-50 text-indigo-600 border-indigo-200',
];

function getTagPalette(tagsCustomID: number): string {
  if (!Number.isFinite(tagsCustomID) || tagsCustomID <= 0) {
    return TAG_COLORS[0];
  }
  return TAG_COLORS[Math.abs(tagsCustomID) % TAG_COLORS.length];
}

function getInitials(title: string): string {
  const cleaned = title.trim();
  if (!cleaned) return '?';
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * Single tag rendering with an on-hover tooltip showing the tag title
 * (bold) plus per-patient description (body). Mirrors the legacy
 * `initToolTip` qTip behavior from
 * `assets-ftp/js/patient-details-test.js`.
 *
 * The icon image streams through `useTagIcon`, which fetches
 * `GET /tags-custom/{id}/icon` and decodes the base64 payload into a
 * cached blob URL.
 */
export function TagIconBadge({
  tag,
  variant = 'pill',
  iconSize,
  className,
  onClick,
  trailing,
}: TagIconBadgeProps): JSX.Element {
  const hasIconFile = Boolean(tag.icon && tag.icon.trim() !== '');
  const iconUrl = useTagIcon(hasIconFile ? tag.tagsCustomID : null);
  const description = (tag.description ?? '').trim();

  const resolvedIconSize = iconSize ?? (variant === 'icon' ? 20 : 14);

  let trigger: React.ReactNode;

  if (variant === 'icon') {
    // Legacy-parity icon-only rendering. Fall back to a small initials
    // disc when the icon file is missing/unset/loading so the tag still
    // has a hoverable surface that conveys identity.
    const dimensionStyle: React.CSSProperties = {
      width: resolvedIconSize,
      height: resolvedIconSize,
    };

    const visual = iconUrl ? (
      <img
        src={iconUrl}
        alt={tag.title}
        width={resolvedIconSize}
        height={resolvedIconSize}
        style={dimensionStyle}
        className="rounded-sm select-none"
        draggable={false}
      />
    ) : (
      <span
        style={{ ...dimensionStyle, fontSize: Math.max(8, Math.round(resolvedIconSize * 0.45)) }}
        className="inline-flex items-center justify-center rounded-full bg-slate-100 text-slate-600 font-semibold uppercase border border-slate-200"
        aria-label={tag.title}
      >
        {getInitials(tag.title)}
      </span>
    );

    trigger = (
      <span
        className={cn(
          'inline-flex items-center gap-0.5 align-middle',
          onClick ? 'cursor-pointer' : 'cursor-default',
          className,
        )}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
      >
        {visual}
        {trailing}
      </span>
    );
  } else {
    const palette = getTagPalette(tag.tagsCustomID);
    trigger = (
      <span
        className={cn(
          'inline-flex items-center gap-1 px-2 py-0.5 rounded border',
          'text-[11px] font-semibold uppercase tracking-wide leading-tight',
          palette,
          onClick ? 'cursor-pointer' : 'cursor-default',
          className,
        )}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
      >
        <span className="whitespace-nowrap">{tag.title}</span>
        {iconUrl && (
          <img
            src={iconUrl}
            alt=""
            width={resolvedIconSize}
            height={resolvedIconSize}
            style={{ width: resolvedIconSize, height: resolvedIconSize }}
            className="rounded-sm select-none shrink-0"
            draggable={false}
          />
        )}
        {trailing}
      </span>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent side="bottom" sideOffset={4} className="max-w-xs px-3 py-2">
        <p className="text-[11px] font-semibold text-white/90 uppercase tracking-wide mb-0.5">
          {tag.title}
        </p>
        {description !== '' && (
          <p className="text-xs text-white/90 whitespace-pre-wrap">{description}</p>
        )}
        {(tag.createDate || tag.addedByName) && (
          <p className="text-[10px] text-white/60 mt-1">
            Added: {tag.createDate ? formatTagDate(tag.createDate) : ''}
            {tag.addedByName ? ` by ${tag.addedByName}` : ''}
          </p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

export interface TagIconRowProps {
  tags: TagIconTag[];
  variant?: TagBadgeVariant;
  iconSize?: number;
  gapClassName?: string;
  className?: string;
  emptyFallback?: React.ReactNode;
  /** Per-tag click handler (e.g. to open the note editor). */
  onTagClick?: (tag: TagIconTag, event: React.MouseEvent<HTMLSpanElement>) => void;
  /** Per-tag trailing decoration (e.g. note marker). */
  renderTrailing?: (tag: TagIconTag) => React.ReactNode;
  /** Per-tag className override (e.g. opacity for pending-add tags). */
  getTagClassName?: (tag: TagIconTag) => string | undefined;
}

/**
 * Lays out a row of `TagIconBadge`s with consistent spacing. Used on the
 * patient detail header, demographics tab, sidebar profile, calendar
 * popover (pill variant), and patients list grid (icon variant).
 */
export function TagIconRow({
  tags,
  variant = 'pill',
  iconSize,
  gapClassName = 'gap-1.5',
  className,
  emptyFallback = null,
  onTagClick,
  renderTrailing,
  getTagClassName,
}: TagIconRowProps): JSX.Element | null {
  if (!tags || tags.length === 0) {
    if (emptyFallback === null) return null;
    return <>{emptyFallback}</>;
  }

  return (
    <div className={cn('flex flex-wrap items-center', gapClassName, className)}>
      {tags.map((tag, index) => (
        <TagIconBadge
          key={`${tag.tagsCustomID}-${index}`}
          tag={tag}
          variant={variant}
          iconSize={iconSize}
          onClick={onTagClick ? (event) => onTagClick(tag, event) : undefined}
          trailing={renderTrailing ? renderTrailing(tag) : undefined}
          className={getTagClassName ? getTagClassName(tag) : undefined}
        />
      ))}
    </div>
  );
}
